import {
  SubwalletBalanceModalBottomSheet,
} from '@/components/shared/SubwalletBalance/SubwalletBalanceModalBottomSheet';
import { SubwalletCard } from '@/components/shared/SubwalletBalance/SubwalletCard';
import {
  groupWalletBalances,
  programYearForGroup,
} from '@/components/shared/SubwalletBalance/walletCardGrouping';
import { QueryKeys } from '@/constants/QueryKeys';
import { UserApi } from '@/utilities/api/UserApi';
import { ApiUser, ResolvedApiSubwalletBalance } from '@api/interfaces';
import { Stack, SxProps, Theme } from '@mui/material';
import { calculateUnexpiredPoints, isBalanceLocked } from '@shared/utilities';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface LockedSubwalletCardsProps {
  accessToken?: string,
  brandColor?: string,
  customer: ApiUser,
  hideBorder?: boolean,
  /** Omit to fetch, mirroring SubwalletList, so callers holding unresolved balances can opt out. */
  subwalletBalances?: ResolvedApiSubwalletBalance[],
  sx?: SxProps<Theme>,
}

/** The locked program years, shown beside Reserved points rather than among the spendable ones. */
export const LockedSubwalletCards = ({
  accessToken,
  brandColor,
  customer,
  hideBorder,
  subwalletBalances,
  sx,
}: LockedSubwalletCardsProps) => {
  const { t } = useTranslation();
  const [selectedBalanceGroup, setSelectedBalanceGroup] =
    useState<ResolvedApiSubwalletBalance[]>([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const { data } = useQuery(
    [QueryKeys.LIST_SUBWALLET_BALANCES, customer.id],
    () => UserApi.getSubwalletBalances(customer.id),
    { enabled: !subwalletBalances },
  );

  const lockedGroups = groupWalletBalances(subwalletBalances ?? data)
    .filter((group) => isBalanceLocked(group[0]) && calculateUnexpiredPoints(group) !== 0);

  if (!lockedGroups.length) {
    return null;
  }

  return (
    <Stack gap="16px" sx={sx}>
      {lockedGroups.map((group, idx) => {
        const year = programYearForGroup(group);
        return (
          <SubwalletCard
            balances={group}
            hideBorder={hideBorder}
            key={idx}
            lockedUntilDate={group[0].lockedUntilDate}
            loyaltySkus={[]}
            onClick={() => {
              setSelectedBalanceGroup(group);
              setShowDetailsModal(true);
            }}
            title={year
              ? t($ => $.wallet.programYearName, { name: group[0].subwallet.name, year })
              : undefined}
          />
        );
      })}
      {showDetailsModal && (
        <SubwalletBalanceModalBottomSheet
          accessToken={accessToken}
          balances={selectedBalanceGroup}
          brandColor={brandColor}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </Stack>
  );
};
